import React, { useEffect } from 'react';
import { Alert, Autocomplete, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetClientOssCode } from '../../../../hooks/datarefs';
import { useInterventionCreation } from './context/interventionCreationContext';
import { ClientOption } from './types';

/**
 * Champ resserré : la charte impose un rayon de 20px qui donne du volume au champ, et
 * celui-ci partage sa ligne avec le titre de l'écran.
 *
 * On agit sur les deux leviers qui réduisent réellement la hauteur — retraits verticaux
 * du conteneur et corps du texte — plutôt que de figer une hauteur. Le libellé flottant
 * reste tel quel : il porte le nom accessible et l'astérisque d'obligation.
 */
const COMPACT_FIELD_SX = {
  '& .MuiOutlinedInput-root': { paddingTop: '1px', paddingBottom: '1px' },
  '& .MuiAutocomplete-input': { fontSize: '0.875rem' }
};

export default function ClientSelector() {
  const { t } = useTranslation();
  const { data: clients } = useGetClientOssCode();
  const { state, dispatch } = useInterventionCreation();

  const isSingleClient = clients?.length === 1;
  const selectedClient: ClientOption | null =
    clients?.find((client) => client.value === state.clientId) ?? null;

  // Le client unique doit être sélectionné pour tout l'écran, pas seulement affiché ici :
  // la valeur vit dans le context, donc elle ne peut pas être dérivée au rendu.
  useEffect(() => {
    if (isSingleClient && clients && state.clientId === null) {
      dispatch({
        type: 'selectClient',
        clientId: clients[0].value,
        clientLabel: clients[0].label
      });
    }
  }, [isSingleClient, clients, state.clientId, dispatch]);

  if (clients && clients.length === 0) {
    return <Alert severity="info">{t('interventionCreation.noClients')}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 360 }}>
      <Autocomplete
        disablePortal
        sx={COMPACT_FIELD_SX}
        disabled={!clients || isSingleClient}
        options={clients ?? []}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedClient}
        onChange={(_, client) =>
          dispatch({
            type: 'selectClient',
            clientId: client?.value ?? null,
            clientLabel: client?.label ?? null
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${t('interventionCreation.client')}*`}
            variant="outlined"
            fullWidth
            size="small"
          />
        )}
      />
    </Box>
  );
}
